import { lazy, useContext, useEffect } from 'react'
import { Route, Routes } from 'react-router-dom'

import AdmissionRouter from '@/app/routes/admission.router.jsx'
import AssessmentRouter from '@/app/routes/assessment.router.jsx'
import InterventionRouter from '@/app/routes/intervention.router.jsx'
import LivebookRouter from '@/app/routes/livebook.router.jsx'
import InternshipRouter from '@/app/routes/internship.router'
import ShowcaseRouter from '@/app/routes/showcase.router.jsx'
import LogIn from '@/components/LogIn'
import RoleMiddleware from '@/components/RoleMiddleware.jsx'
import { AuthContext } from '@/contexts/AuthContext.js'
import { AUTHOR, MENTOR, STUDENT_PLUS } from '@/enums/userRoles.js'
import DefaultLayout from '@/layouts/Default.jsx'
import { isProd } from '@/utils/common.js'

const ForbiddenPage = lazy(() => import('@/pages/403'))
const NotFoundPage = lazy(() => import('@/pages/404'))
const HealthCheckPage = lazy(() => import('@/pages/HealthCheck'))
const QuizPage = lazy(() => import('@/pages/Quiz'))
const MaintenancePage = lazy(() => import('@/pages/Maintenance'))
const AdmissionNext = lazy(() => import('@/pages/AdmissionNext'))
const AdmissionNextChecks = lazy(() =>
    import('@/pages/AdmissionNext/HealthCheck')
)
const Dojo = lazy(() => import('@/pages/Dojo'))
const Workout = lazy(() => import('@/pages/Dojo/Workout'))
const Konfident = lazy(() => import('@/deprecated/Konfident'))
const Attendance = lazy(() => import('@/pages/Attendance/Attendance'))
const Signal = lazy(() => import('@/pages/Signal'))
const AttemptResult = lazy(() => import('@/pages/Attempt/Result'))
const BrowserEnrollment = lazy(() => import('@/pages/BrowserEnrollment'))

const studentAppUrl = import.meta.env.VITE_STUDENT_NEXT_APP_BASE_URL
const fallbackAppUrl = 'https://dev-app.kalvium.org/'
const appBaseUrl = studentAppUrl || (!isProd() ? fallbackAppUrl : '')

// Redirect component for livebooks
const LivebooksRedirect = () => {
    useEffect(() => {
        window.location.replace(`${appBaseUrl}livebooks`)
    }, [])
    return null
}

const MarkersRedirect = () => {
    useEffect(() => {
        window.location.replace(`${appBaseUrl}markers`)
    }, [])
    return null
}

const AccountRedirect = () => {
    useEffect(() => {
        window.location.replace(`${appBaseUrl}account`)
    }, [])
    return null
}

const HomeRedirect = () => {
    useEffect(() => {
        if (!appBaseUrl) return
        window.location.replace(`${appBaseUrl}dashboard`)
    }, [])
    return null
}

const Router = () => {
    const { kalviumKc } = useContext(AuthContext)

    const isMaintenanceWindowEnabled =
        import.meta.env.VITE_IS_MAINTENANCE_WINDOW_ENABLED === 'yes'

    if (isMaintenanceWindowEnabled) {
        return (
            <Routes>
                <Route path={'*'} element={<MaintenancePage />} />
            </Routes>
        )
    }

    return (
        <Routes>
            {/* Redirect route for /livebooks/* */}
            <Route path={'/livebooks/*'} element={<LivebooksRedirect />} />
            <Route path={'/markers/*'} element={<MarkersRedirect />} />
            <Route path={'/account/*'} element={<AccountRedirect />} />
            <Route
                path="/"
                element={kalviumKc.isLoggedIn() ? <DefaultLayout /> : <LogIn />}
            >
                <Route index element={<HomeRedirect />} />
                <Route
                    path={'/livebooks-legacy/*'}
                    element={
                        <RoleMiddleware roles={[AUTHOR, MENTOR, STUDENT_PLUS]}>
                            <LivebookRouter />
                        </RoleMiddleware>
                    }
                />
                <Route path={'/interventions'} element={InterventionRouter} />
                <Route
                    path={'dojo'}
                    element={
                        <RoleMiddleware roles={[AUTHOR, MENTOR, STUDENT_PLUS]}>
                            <Dojo />
                        </RoleMiddleware>
                    }
                />
                <Route path={'konfident'} element={<Konfident />} />
                <Route path={'attendance'} element={<Attendance />} />
                <Route path={'signal/:slugOrId'} element={<Signal />} />
                <Route path={'/internships/*'} element={InternshipRouter} />
            </Route>
            {/* routes without sidebar */}
            <Route path={'assessment/*'} element={<AssessmentRouter />} />
            <Route path={'attempt/:attemptId'} element={<AttemptResult />} />
            <Route path={'quiz/:axSlug'} element={<QuizPage />} />
            <Route path={'enroll'} element={<BrowserEnrollment />} />
            <Route path={'check'} element={<HealthCheckPage />} />
            <Route
                path={'dojo/workout/:workout_slug'}
                element={
                    <RoleMiddleware roles={[AUTHOR, MENTOR, STUDENT_PLUS]}>
                        <Workout />
                    </RoleMiddleware>
                }
            />

            <Route path={'/admission/*'} element={AdmissionRouter} />
            <Route
                exact
                path={'admission-next/check'}
                element={<AdmissionNextChecks />}
            />
            <Route
                exact
                path={'admission-next/:ax_slug'}
                element={<AdmissionNext />}
            />
            <Route path={'/showcase'} element={ShowcaseRouter} />
            <Route path={'403'} element={<ForbiddenPage />} />
            <Route path={'*'} element={<NotFoundPage />} />
        </Routes>
    )
}

export default Router
